import React from "react";
import { Text, Element as SlateElement } from "slate";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { WorkspaceMember } from "@common/types/workspace";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import {
    type CustomText,
    type MentionElement,
    type LinkElement,
    type CustomElement,
    type SlateInlineChild,
    type SlateValue,
} from "./slateTypes";
import { getFullName, isChecklistItem } from "./slateHelpers";
import { EntityRefPill } from "./entityRef/EntityRefPill";
import styles from "./SlateRenderer.module.scss";

const renderText = (node: CustomText, index: number): React.ReactNode => {
    let content: React.ReactNode = node.text;

    if (node.bold) {
        content = <strong key={`bold-${index}`}>{content}</strong>;
    }
    if (node.italic) {
        content = <em key={`italic-${index}`}>{content}</em>;
    }
    if (node.underline) {
        content = <u key={`underline-${index}`}>{content}</u>;
    }

    return <span key={index}>{content}</span>;
};

const MentionWithTooltip: React.FC<{
    element: MentionElement;
    workspaceMembers?: WorkspaceMember[];
}> = ({ element, workspaceMembers }) => {
    const member = workspaceMembers?.find((m) => m.user.id === element.userId);
    const user = member?.user;
    const fullName = getFullName(user, element.name);

    return (
        <Tooltip
            content={
                <div className={styles.mentionTooltip}>
                    {user && <UserAvatar user={user} size="sm" aria-hidden />}
                    <span>{fullName}</span>
                </div>
            }
        >
            <span className={styles.mention} data-user-id={element.userId}>
                @{element.name}
            </span>
        </Tooltip>
    );
};

const renderMention = (
    element: MentionElement,
    index: number,
    workspaceMembers?: WorkspaceMember[],
): React.ReactNode => (
    <MentionWithTooltip
        key={index}
        element={element}
        workspaceMembers={workspaceMembers}
    />
);

const renderLink = (element: LinkElement, index: number): React.ReactNode => (
    <a
        key={index}
        href={element.url}
        className={styles.link}
        target="_blank"
        rel="noopener noreferrer"
    >
        {element.children.map((child, i) => renderText(child, i))}
    </a>
);

const renderInlineChild = (
    child: SlateInlineChild,
    index: number,
    workspaceMembers?: WorkspaceMember[],
): React.ReactNode => {
    if (Text.isText(child)) {
        return renderText(child, index);
    }
    if (child.type === "mention") {
        return renderMention(child, index, workspaceMembers);
    }
    if (child.type === "link") {
        return renderLink(child, index);
    }
    if (child.type === "entityRef") {
        return <EntityRefPill key={index} entityRef={child} />;
    }
    return null;
};

const renderElement = ({
    element,
    path,
    workspaceMembers,
    onToggleCheck,
}: {
    element: CustomElement;
    path: number[];
    workspaceMembers?: WorkspaceMember[];
    onToggleCheck?: (path: number[]) => void;
}): React.ReactNode => {
    const key = path.join(".");
    if (element.type === "mention") {
        return renderMention(
            element,
            path[path.length - 1] ?? 0,
            workspaceMembers,
        );
    }
    if (element.type === "link") {
        return renderLink(element, path[path.length - 1] ?? 0);
    }
    if (element.type === "entityRef") {
        return (
            <EntityRefPill
                key={path[path.length - 1] ?? 0}
                entityRef={element}
            />
        );
    }
    if (element.type === "bulleted-list") {
        return (
            <ul key={key}>
                {element.children.map((item, i) =>
                    renderElement({
                        element: item,
                        path: [...path, i],
                        workspaceMembers,
                        onToggleCheck,
                    }),
                )}
            </ul>
        );
    }
    if (element.type === "numbered-list") {
        return (
            <ol key={key}>
                {element.children.map((item, i) =>
                    renderElement({
                        element: item,
                        path: [...path, i],
                        workspaceMembers,
                        onToggleCheck,
                    }),
                )}
            </ol>
        );
    }
    if (element.type === "list-item") {
        const children = element.children.map((child, i) =>
            renderElement({
                element: child,
                path: [...path, i],
                workspaceMembers,
                onToggleCheck,
            }),
        );
        if (!isChecklistItem(element)) {
            return <li key={key}>{children}</li>;
        }
        return (
            <li key={key} className={styles.checkItem}>
                <span className={styles.checkControl}>
                    <Checkbox
                        checked={element.checked}
                        disabled={onToggleCheck == null}
                        aria-label={
                            element.checked
                                ? "Mark incomplete"
                                : "Mark complete"
                        }
                        onCheckedChange={() => onToggleCheck?.(path)}
                    />
                </span>
                {children}
            </li>
        );
    }
    if (element.type === "variable") {
        return null;
    }

    const isEmptyParagraph =
        element.children.length === 1 &&
        Text.isText(element.children[0]) &&
        element.children[0].text === "";

    if (isEmptyParagraph) {
        return (
            <p key={key} className="mb-0">
                <br />
            </p>
        );
    }

    const children = element.children.map((child, i) =>
        renderInlineChild(child, i, workspaceMembers),
    );

    return (
        <p key={key} className="mb-0">
            {children}
        </p>
    );
};

interface SlateRendererProps {
    content: string;
    onToggleCheck?: (path: number[]) => void;
}

export const SlateRenderer: React.FC<SlateRendererProps> = ({
    content,
    onToggleCheck,
}) => {
    const { data: workspaceMembers } = useCurrentWorkspaceMembers();

    let value: SlateValue;

    try {
        value = JSON.parse(content) as SlateValue;
    } catch {
        // Fallback for plain text content
        return <div className={styles.messageText}>{content}</div>;
    }

    return (
        <div className={styles.messageText}>
            {value.map((node, index) => {
                if (SlateElement.isElement(node)) {
                    return renderElement({
                        element: node as CustomElement,
                        path: [index],
                        workspaceMembers,
                        onToggleCheck,
                    });
                }
                return null;
            })}
        </div>
    );
};
